import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'hooks/apollo';
import { ACCOUNT_TYPES } from '../../queries';
import { RAW_FINANCIAL_INDICATORS_QUERY } from '../../../../financialIndicators/components/list/queries';
import { ADD_UPDATE_ACCOUNT_STATEMENT_DETAIL, REMOVE_ACCOUNT_STATEMENT_DETAIL } from '../../mutations';
import { required, shape, errorMessage } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { get, useChangeTrackingLens, set, over } from '@totalsoft/change-tracking-react';
import AccountStatementDetailsComponent from './AccountStatementDetailsComponent';

export const accStatementDetailsValidator = shape({
  accountSymbol: required |> errorMessage('AccountStatement.Details.Validators.AccountSymbolRequired'),
  accountName: required |> errorMessage('AccountStatement.Details.Validators.AccountNameRequired'),
  accountTypeId: required |> errorMessage('AccountStatement.Details.Validators.AccountTypeIdRequired'),
  attributName: required |> errorMessage('AccountStatement.Details.Validators.AttributNameRequired')
});

const AccountStatementDetailsContainer = ({ data, configId, canWrite, usedByPartner }) => {
  const { t } = useTranslation();
  const [lens, , resetLens] = useChangeTrackingLens({ ...data, isInEditing: false, isSaving: false });
  const statementDetail = lens |> get;
  
  const [validation, validate] = useDirtyFieldValidation(accStatementDetailsValidator);

  const { data: accountTypesListData } = useQuery(ACCOUNT_TYPES);
  const accountTypes = useMemo(() => accountTypesListData?.dictionaryAccountTypes || [], [accountTypesListData]);

  const { data: financialIndicatorData } = useQuery(RAW_FINANCIAL_INDICATORS_QUERY);
  const financialIndicatorList = useMemo(() => financialIndicatorData?.financialIndicators || [], [financialIndicatorData]);

  // #region effect
  useEffect(() => {
    resetLens({ ...data, isInEditing: false, isSaving: false });
  }, [data, financialIndicatorList, resetLens]);

  // #endregion effect

  // #region alter

  const [saveAccountStatementDetail] = useMutation(ADD_UPDATE_ACCOUNT_STATEMENT_DETAIL, {
    onError: () => {
      set(lens.isSaving)(false);
    }
  });

  const [deleteAccountStatementDetail] = useMutation(REMOVE_ACCOUNT_STATEMENT_DETAIL, {
    onError: () => {
      set(lens.isSaving)(false);
    }
  });

  //endregion alter

  // #region handlers

  const handleCancelEdit = useCallback(() => {
    const original = data;
    over(lens, prevValue => ({ ...prevValue, ...original, isInEditing: false }));
  }, [data, lens]);

  const handleSave = useCallback(() => {
    if (!validate(statementDetail)) return;
    set(lens.isSaving)(true);

    saveAccountStatementDetail({
      variables: {
        input: {
          configDetailId: parseInt(statementDetail.configDetailId),
          orderNo: parseInt(statementDetail.orderNo),
          configId: parseInt(configId),
          accountSymbol: statementDetail.accountSymbol,
          accountName: statementDetail.accountName,
          accountTypeId: parseInt(statementDetail.accountTypeId),
          financialIndicatorId: parseInt(statementDetail.financialIndicatorId),
          attributName: statementDetail.attributName,
          visibleYN: statementDetail.visibleYN,
          readOnlyYN: statementDetail.readOnlyYN
        }
      }
    });
  }, [validate, statementDetail, lens.isSaving, saveAccountStatementDetail, configId]);

  const handleDelete = useCallback(() => {
    if (window.confirm(t('AccountStatement.DeleteDetailConfirmation'))) {
      deleteAccountStatementDetail({ variables: { input: { configDetailId: data.configDetailId } } });
    }
  }, [data.configDetailId, deleteAccountStatementDetail, t]);
  //  #endregion handlers

  return (
    <AccountStatementDetailsComponent
      canWrite={canWrite}
      statementDetailLens={lens}
      statementDetail={statementDetail}
      accountTypes={accountTypes}
      financialIndicatorList={financialIndicatorList}
      onSave={handleSave}
      onCancelEdit={handleCancelEdit}
      onDelete={handleDelete}
      validation={validation}
      usedByPartner={usedByPartner}
    />
  );
};

AccountStatementDetailsContainer.propTypes = {
  data: PropTypes.object.isRequired,
  configId: PropTypes.number,
  canWrite: PropTypes.bool,
  usedByPartner: PropTypes.bool
};

export default AccountStatementDetailsContainer;
